import { ref, type Ref } from 'vue';

type AsyncFn = (...args: never[]) => Promise<unknown>;

/**
 * Envuelve una acción async con estado loading y bloqueo de ejecuciones concurrentes.
 */
export function useAsyncAction<T extends AsyncFn>(fn: T) {
  const loading = ref(false);
  let inFlight: Promise<Awaited<ReturnType<T>>> | null = null;

  async function run(
    ...args: Parameters<T>
  ): Promise<Awaited<ReturnType<T>> | undefined> {
    if (loading.value) {
      return inFlight ?? undefined;
    }

    loading.value = true;
    inFlight = fn(...args) as Promise<Awaited<ReturnType<T>>>;

    try {
      return await inFlight;
    } finally {
      loading.value = false;
      inFlight = null;
    }
  }

  return {
    loading: loading as Readonly<Ref<boolean>>,
    run,
  };
}

/**
 * Varios flags loading en un solo objeto (p. ej. approve / reject).
 */
export function useAsyncActions<T extends Record<string, AsyncFn>>(actions: T) {
  const loadingMap = ref(
    Object.fromEntries(Object.keys(actions).map((key) => [key, false])) as Record<
      keyof T,
      boolean
    >,
  );

  const runners = {} as {
    [K in keyof T]: (...args: Parameters<T[K]>) => Promise<Awaited<ReturnType<T[K]>> | undefined>;
  };

  for (const key of Object.keys(actions) as (keyof T)[]) {
    const fn = actions[key];
    let inFlight: Promise<unknown> | null = null;

    runners[key] = async (...args: Parameters<T[typeof key]>) => {
      if (loadingMap.value[key]) {
        return (inFlight as Promise<Awaited<ReturnType<T[typeof key]>>>) ?? undefined;
      }

      loadingMap.value[key] = true;
      inFlight = fn(...args);

      try {
        return (await inFlight) as Awaited<ReturnType<T[typeof key]>>;
      } finally {
        loadingMap.value[key] = false;
        inFlight = null;
      }
    };
  }

  const isLoading = (key?: keyof T) => {
    if (!key) return Object.values(loadingMap.value).some(Boolean);
    return loadingMap.value[key];
  };

  return {
    loading: loadingMap,
    run: runners,
    isLoading,
  };
}
